import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Headline from "components/text.components/headline.component/headline.component";
import { RunningText } from "components/text.components/running.text.component/running.text.component";
import Column from "components/general.compoenents/column.component/column.component";
import OutlinedTextInput from "components/input.components/outlined.text.input.component/outlined.text.input.component";
import SizedContainer from "components/general.compoenents/sized.container.component/sized.container.component";
import { ContainerSizes } from "globals/enums/global.enum";
import FilledButton from "components/input.components/filled.button.component/filled.button.component";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { saveAccessToken } from "globals/helpers/storage.helper";
import { getEmailValidationPattern } from "globals/helpers/validation.helper";
import previewIcon from "assets/illustrations/gymo_app_preview.png";
import gymoIcon from "assets/icons/gymo_logo_with_lettering.png";
import { Logging } from "globals/helpers/logging.helper";
import { HttpUserService } from "services/httpClients/http.user.client";
import "./password.reset.page.scss";
import Row from "components/general.compoenents/row.component/row.component";

const PasswordResetPage = (): JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);

  // set token from params to state
  useEffect(() => {
    const token = params.get("token");

    if (token) {
      saveAccessToken(token);
    }
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const handleResetPassword = async (data: any): Promise<void> => {
    try {
      const response = await HttpUserService.getInstance().handleResetPassword({
        email: data.email,
        newPassword: data.newPassword,
      });

      if (!response) {
        navigate("/sign-in");
        toast.error("Passwort konnte nicht zurückgesetzt werden");
        return;
      }

      navigate("/sign-in");
      toast.success("Passwort wurde erfolgreich zurückgesetzt");
    } catch (err) {
      Logging.error({
        className: "PasswordResetPage",
        methodName: "handleResetPassword",
        message: "Passwort konnte nicht zurückgesetzt werden",
        exception: err,
        showAlert: true,
      });
    }
  };

  const _buildPasswordResetContent = (): JSX.Element => {
    return (
      <div className="password-reset-form-container">
        <div className="password-reset-form-wrapper">
          <form
            className="password-reset-form"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={handleSubmit(handleResetPassword, (errors) => {
              toast.error("Bitte überprüfe deine Eingaben");
            })}
            noValidate
          >
            <SizedContainer size={ContainerSizes.L}>
              <Column alignItems="flex-start">
                <Headline className="mb-15">
                  Hey {params.get("senderName")}! Hier kannst du dein neues
                  Passwort eintragen
                </Headline>
                <RunningText className="mb-20">
                  Setze ein neues Passwort für deinen Account
                </RunningText>
                <OutlinedTextInput
                  label="Email"
                  type="email"
                  inputRef={register("email", {
                    required: "Email is required",
                    pattern: {
                      value: getEmailValidationPattern(),
                      message: "Please enter a valid email",
                    },
                  })}
                  validationMessage={errors.email?.message?.toString()}
                />
                <OutlinedTextInput
                  label="Neues Passwort"
                  type="password"
                  inputRef={register("newPassword", {
                    required: "Password is required",
                  })}
                  validationMessage={errors.newPassword?.message?.toString()}
                />
                <FilledButton
                  label="Passwort zurücksetzen"
                  className="mt-15 full-width"
                  type="submit"
                />
              </Column>
            </SizedContainer>
          </form>
        </div>
      </div>
    );
  };

  const _buildPreview = (): JSX.Element => {
    return (
      <div className="password-reset-preview-container">
        <img className="gymo-logo" src={gymoIcon} />
        <Headline className="password-reset-header">{`The biggest smallest\nFitness Software.`}</Headline>
        <img className="password-reset-preview-image" src={previewIcon} />
      </div>
    );
  };

  return (
    <Row className="password-reset-page">
      <div className="password-reset-page-wrapper">
        {_buildPreview()}
        <div className="password-reset-form-wrapper">
          {_buildPasswordResetContent()}
        </div>
      </div>
    </Row>
  );
};

export default PasswordResetPage;
